/* Numbers the way a trader reads them: en-US grouping, tabular figures (set in CSS),
   a real minus sign (U+2212), New York times written "12:16 ET". */

export const MINUS = '−';

const fmtCache = new Map<number, Intl.NumberFormat>();
function nf(d: number): Intl.NumberFormat {
  let f = fmtCache.get(d);
  if (!f) {
    f = new Intl.NumberFormat('en-US', { minimumFractionDigits: d, maximumFractionDigits: d });
    fmtCache.set(d, f);
  }
  return f;
}

export const isNum = (x: unknown): x is number => typeof x === 'number' && Number.isFinite(x);

/** 31,256.75 ; negative with U+2212 ; '–' when missing */
export function price(v: number | null | undefined, d = 2): string {
  if (!isNum(v)) return '–';
  const s = nf(d).format(Math.abs(v));
  return v < 0 ? MINUS + s : s;
}

/** A strike: no decimals when it is a whole number. */
export function strike(v: number | null | undefined): string {
  if (!isNum(v)) return '–';
  return Math.abs(v - Math.round(v)) < 1e-9 ? price(v, 0) : price(v, 2);
}

/** Always signed: +1.25 / −1.25 */
export function signed(v: number | null | undefined, d = 2): string {
  if (!isNum(v)) return '–';
  return (v >= 0 ? '+' : MINUS) + nf(d).format(Math.abs(v));
}

/** Signed percent from a ratio already in percent units: +0.13% */
export function pct(v: number | null | undefined, d = 2, sign = true): string {
  if (!isNum(v)) return '–';
  return (sign ? (v >= 0 ? '+' : MINUS) : v < 0 ? MINUS : '') + nf(d).format(Math.abs(v)) + '%';
}

/** Dollar notional: +$2.35B, −$960M, +$12K */
export function usd(v: number | null | undefined, sign = true): string {
  if (!isNum(v)) return '–';
  const a = Math.abs(v);
  const body = a >= 1e9 ? '$' + nf(2).format(a / 1e9) + 'B' : a >= 1e6 ? '$' + nf(0).format(a / 1e6) + 'M' : a >= 1e3 ? '$' + nf(0).format(a / 1e3) + 'K' : '$' + nf(0).format(a);
  return sign ? (v < 0 ? MINUS : '+') + body : (v < 0 ? MINUS : '') + body;
}

/** Contract counts: 12.4k, 1.2M */
export function count(v: number | null | undefined, sign = false): string {
  if (!isNum(v)) return '–';
  const a = Math.abs(v);
  const body = a >= 1e6 ? nf(1).format(a / 1e6) + 'M' : a >= 1e4 ? nf(0).format(a / 1e3) + 'k' : a >= 1e3 ? nf(1).format(a / 1e3) + 'k' : nf(0).format(a);
  return (v < 0 ? MINUS : sign ? '+' : '') + body;
}

/** "buy $512M" / "sell $512M": the direction of a hedge flow */
export const flow = (v: number): string => (v >= 0 ? 'buy ' : 'sell ') + usd(v, false).replace(MINUS, '');

const nyClock = new Intl.DateTimeFormat('en-GB', { timeZone: 'America/New_York', hour: '2-digit', minute: '2-digit', hourCycle: 'h23' });
const nyDay = new Intl.DateTimeFormat('en-US', { timeZone: 'America/New_York', weekday: 'short', month: 'short', day: 'numeric' });

/** An instant (ms or ISO with an offset) as "12:16 ET" */
export const etTime = (t: number | string): string => nyClock.format(new Date(t)) + ' ET';

/** The data's own clock: Cboe sends a New York wall time without an offset ("2026-10-05T12:38:49"). */
export function dataClock(naive: string | null | undefined): string | null {
  if (!naive) return null;
  const m = /T(\d{2}):(\d{2})/.exec(naive);
  return m ? `${m[1]}:${m[2]} ET` : null;
}

/** "Mon, Oct 5" for an ISO day (noon avoids any timezone edge) */
export const day = (iso: string): string => nyDay.format(new Date(iso + 'T12:00:00Z'));

/** Short weekday of an ISO day: "Fri" */
export const weekday = (iso: string): string => day(iso).split(',')[0] ?? iso;
